// src/pages/Login.jsx

import React, { useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import GradientText from '../components/GradientText';
import GoogleIcon from '../components/icons/GoogleIcon';
import '../styles/Login.css';

const Loader = () => <div className="loader"></div>;

export default function Login() { 
  const supabase = useSupabaseClient();
  // ✨ 【【【核心修正 1】】】 ✨
  // 引入一個新的狀態來管理三種模式：'login', 'signup', 'resetPassword'
  const [mode, setMode] = useState('login'); 
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // 根據 mode 衍生出布林值，方便在 JSX 中使用
  const isLoginMode = mode === 'login';
  const isSignupMode = mode === 'signup';
  const isResetMode = mode === 'resetPassword';

  const setView = (newMode) => {
    setMode(newMode);
    setMessage({ text: '', type: '' });
  };

  // ✨ Google 登入功能實作
  const handleGoogleLogin = async () => {
    try {
      setLoading(true); // 顯示載入動畫
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          // 確保這裡的路徑與你在 Supabase Dashboard 設定的 Redirect URL 一致
          redirectTo: window.location.origin + '/auth/callback',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) throw error;
      // 注意：OAuth 會跳轉頁面，所以這裡不需要 setLoading(false)
    } catch (error) {
      setMessage({ text: error.message, type: 'error' });
      setLoading(false);
    }
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();
    // 在提交時不清空訊息，以便在失敗時能顯示它
    // setMessage({ text: '', type: '' });

    if (isSignupMode && password !== confirmPassword) {
      setMessage({ text: 'Passwords do not match.', type: 'error' });
      return;
    }

    setLoading(true);

    try {
      if (isLoginMode) {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        // 登入成功後，由 App.jsx 處理跳轉，這裡什麼都不做
      } else if (isSignupMode) {
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        
        // ✨ 【【【核心修正 2】】】 ✨
        // 只設置成功訊息，不再切換視圖。
        // 讓使用者看到訊息，並可以手動點擊 "Sign In"
        setMessage({ text: 'Account created! Please check your email for verification.', type: 'success' });
        // setView('login'); // 移除這一行！

      } else if (isResetMode) {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: window.location.origin + '/auth/callback',
        });
        if (error) throw error;
        
        // ✨ 【【【核心修正 3】】】 ✨
        // 同樣，只設置成功訊息，讓使用者看到它。
        setMessage({ text: 'If an account exists for this email, a password reset link has been sent.', type: 'success' });
      }
    } catch (error) {
      // 捕獲所有錯誤（登入失敗、註冊失敗等）並顯示
      setMessage({ text: error.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (isLoginMode) return 'Welcome Back!';
    if (isSignupMode) return 'Create an Account';
    return 'Reset Your Password';
  };

  const getSubtitle = () => {
    if (isLoginMode) return 'Sign in to access the TheraLingua AI platform.';
    if (isSignupMode) return 'Get started with TheraLingua AI today.';
    return 'Enter your email to receive a password reset link.';
  };

  return (
    <div className="login-page-wrapper">
      <div className="background-layer dots-background" aria-hidden="true" />
      <div className="content-layer">
        <div className="login-content-wrapper">
          <h1 className="login-page-title">
            <GradientText colors={["#ff9068", "#fd746c", "#ffb88c", "#ff9068"]} animationSpeed={6}>
              TheraLingua AI
            </GradientText>
          </h1>
          <div className="auth-container">
            {message.text && <div className={`message ${message.type}`}>{message.text}</div>}
            <div className="auth-header">
              <h1>{getTitle()}</h1>
              <p>{getSubtitle()}</p>
            </div>
            <form id="auth-form" className="auth-form" onSubmit={handleFormSubmit}>
              <div className="input-group">
                <label htmlFor="email">Email Address</label>
                <input type="email" id="email" name="email" placeholder="you@example.com" required value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              
              {/* ✨ 【【【核心修正 3】】】 ✨ */}
              {/* 只有在登入或註冊模式下才顯示密碼框 */}
              {(isLoginMode || isSignupMode) && (
                <div className="input-group">
                  {/* ✨ 【【【核心修正 1】】】 ✨ */}
                  {/* 將 label 和 "Forgot Password?" 連結包裹在一個 div 中，並使用 Flexbox 對齊 */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <label htmlFor="password">Password</label>
                    {isLoginMode && (
                      <a 
                        onClick={() => setView('resetPassword')} 
                        className="auth-footer-link" 
                        style={{ fontSize: '0.875rem' }} // 保持字體大小
                      >
                        Forgot Password?
                      </a>
                    )}
                  </div>
                  <input 
                    type="password" 
                    id="password" 
                    name="password" 
                    placeholder="••••••••" 
                    required={!isResetMode} 
                    value={password} 
                    onChange={(e) => setPassword(e.target.value)} 
                  />
                </div>
              )}

              {isSignupMode && (
                <div id="confirm-password-group" className="input-group">
                  <label htmlFor="confirm-password">Confirm Password</label>
                  <input type="password" id="confirm-password" name="confirm-password" placeholder="••••••••" required value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
                </div>
              )}

              <button type="submit" id="submit-button" className="submit-btn" disabled={loading}>
                {loading ? <Loader /> : <span>{isResetMode ? 'Send Reset Link' : (isLoginMode ? 'Sign In' : 'Sign Up')}</span>}
              </button>
            </form>

            {!isResetMode && (
              <>
                <div className="auth-divider">
                  <span>OR</span>
                </div>
                <div className="oauth-group">
                  <button type="button" className="google-btn" onClick={handleGoogleLogin} disabled={loading}>
                    <GoogleIcon size={18} />
                    <span>Continue with Google</span>
                  </button>
                </div>
              </>
            )}

            <div className="auth-footer">
              <p>
                {isResetMode ? 'Remembered your password? ' : (isLoginMode ? "Don't have an account? " : 'Already have an an account? ')}
                <a onClick={() => setView(isResetMode || isSignupMode ? 'login' : 'signup')} className="auth-footer-link">
                  {isResetMode || isSignupMode ? 'Sign In' : 'Sign Up'}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
